export type Language = "en" | "pt" | "es";

interface TeamMemberContact {
  phoneDisplay?: string;
  phoneE164?: string;
  whatsappE164?: string;
  instagramUrl?: string;
}

interface TeamMember {
  name: string;
  role: string;
  description: string;
  contact?: TeamMemberContact;
  badges?: string[];
  imageKey?: "primary" | "secondary" | string;
}

export interface Translations {
  nav: {
    services: string;
    portfolio: string;
    team: string;
    contact: string;
    bookNow: string;
  };
  hero: {
    title: string;
    titleAccent: string;
    titleEnd: string;
    subtitle: string;
    bookAppointment: string;
    viewServices: string;
    hours: string;
  };
  services: {
    title: string;
    titleAccent: string;
    subtitle: string;
    bookNow: string;
    kicker?: string;
    badge?: string;
  };
  portfolio: {
    title: string;
    titleAccent: string;
    subtitle: string;
    kicker?: string;
  };
  team: {
    title: string;
    titleAccent: string;
    subtitle: string;
    members: TeamMember[];
  };
  visit: {
    title: string;
    titleAccent: string;
    subtitle: string;
    ourLocation: string;
    openingHours: string;
    getDirections: string;
    closed: string;
    kicker?: string;
    days: {
      monday: string;
      tuesday: string;
      wednesday: string;
      thursday: string;
      friday: string;
      saturday: string;
      sunday: string;
    };
  };
  footer: {
    rights: string;
  };
  notFound: {
    title: string;
    message: string;
    backHome: string;
  };
}

export const translations = {
  en: {
    nav: {
      services: "Services",
      portfolio: "Portfolio",
      team: "Team",
      contact: "Contact",
      bookNow: "Book Now",
    },
    hero: {
      title: "",
      titleAccent: "Marriel",
      titleEnd: "Barber Shop",
      subtitle:
        "Where tradition meets modern craftsmanship. Experience the art of premium grooming in an atmosphere of refined elegance.",
      bookAppointment: "Book Appointment",
      viewServices: "View Services",
      hours: "Tue–Sat: Open · Closed Sunday and Monday",
    },
    services: {
      title: "Our",
      titleAccent: "Services",
      subtitle: "Premium grooming services crafted with precision and care",
      kicker: "PREMIUM BARBERING",
      badge: "PREMIUM",
      bookNow: "Book Now",
    },
    portfolio: {
      title: "Our",
      titleAccent: "Work",
      subtitle: "A showcase of our finest cuts and styles",
      kicker: "OUR WORK",
    },
    team: {
      title: "Meet the",
      titleAccent: "Team",
      subtitle: "Expert craftsmen dedicated to your perfect look",
      members: [
        {
          name: "Marriel",
          role: "Master Barber / Founder",
          description:
            "Specialized in modern cuts — fades, taper fades, and clean finishing.",
          badges: ["Skin Fade", "Taper Fade", "Line-up", "Beard"],
          imageKey: "marriel",
          contact: {
            phoneDisplay: "203-417-3905",
            phoneE164: "+12034173905",
            whatsappE164: "+12034173905",
            instagramUrl: "https://instagram.com/marriel_barber",
          },
        },
      ],
    },
    visit: {
      title: "Visit",
      titleAccent: "Us",
      subtitle: "We look forward to welcoming you",
      ourLocation: "Our Location",
      openingHours: "Opening Hours",
      getDirections: "Get Directions",
      closed: "Closed",
      kicker: "VISIT US",
      days: {
        monday: "Mon",
        tuesday: "Tue",
        wednesday: "Wed",
        thursday: "Thu",
        friday: "Fri",
        saturday: "Sat",
        sunday: "Sun",
      },
    },
    footer: {
      rights: "© Marriel Barber Shop. All rights reserved.",
    },
    notFound: {
      title: "404",
      message: "Oops! Page not found",
      backHome: "Return to Home",
    },
  },

  pt: {
    nav: {
      services: "Serviços",
      portfolio: "Portfólio",
      team: "Equipe",
      contact: "Contato",
      bookNow: "Agendar",
    },
    hero: {
      title: "Barbearia",
      titleAccent: "Marriel",
      titleEnd: "",
      subtitle:
        "Onde a tradição encontra a arte moderna. Experimente o cuidado premium em um ambiente de elegância refinada.",
      bookAppointment: "Agendar Horário",
      viewServices: "Ver Serviços",
      hours: "Ter–Sáb: Aberto · Fechado domingo e segunda",
    },
    services: {
      title: "Nossos",
      titleAccent: "Serviços",
      subtitle: "Serviços de barbearia premium com precisão e cuidado",
      kicker: "BARBEARIA PREMIUM",
      badge: "PREMIUM",
      bookNow: "Agendar",
    },
    portfolio: {
      title: "Nosso",
      titleAccent: "Trabalho",
      subtitle: "Uma vitrine dos nossos melhores cortes e estilos",
      kicker: "NOSSO TRABALHO",
    },
    team: {
      title: "Conheça a",
      titleAccent: "Equipe",
      subtitle: "Profissionais dedicados ao seu visual perfeito",
      members: [
        {
          name: "Marriel",
          role: "Barbeiro Mestre / Fundador",
          description:
            "Especialista em cortes modernos com acabamento preciso e atenção aos detalhes.",
          imageKey: "marriel",
        },
      ],
    },
    visit: {
      title: "Visite",
      titleAccent: "-nos",
      subtitle: "Esperamos recebê-lo em breve",
      ourLocation: "Nossa Localização",
      openingHours: "Horário de Funcionamento",
      getDirections: "Como Chegar",
      closed: "Fechado",
      kicker: "VISITE-NOS",
      days: {
        monday: "Seg",
        tuesday: "Ter",
        wednesday: "Qua",
        thursday: "Qui",
        friday: "Sex",
        saturday: "Sáb",
        sunday: "Dom",
      },
    },
    footer: {
      rights: "© Marriel Barber Shop. Todos os direitos reservados.",
    },
    notFound: {
      title: "404",
      message: "Ops! Página não encontrada",
      backHome: "Voltar para o início",
    },
  },

  es: {
    nav: {
      services: "Servicios",
      portfolio: "Portafolio",
      team: "Equipo",
      contact: "Contacto",
      bookNow: "Reservar",
    },
    hero: {
      title: "",
      titleAccent: "Marriel",
      titleEnd: "Barber Shop",
      subtitle:
        "Donde la tradición se encuentra con la precisión moderna. Disfruta de una experiencia premium en un ambiente elegante.",
      bookAppointment: "Reservar Cita",
      viewServices: "Ver Servicios",
      hours: "Mar–Sáb: Abierto · Cerrado domingo y lunes",
    },
    services: {
      title: "Nuestros",
      titleAccent: "Servicios",
      subtitle: "Servicios de barbería premium con precisión y cuidado",
      kicker: "BARBERÍA PREMIUM",
      badge: "PREMIUM",
      bookNow: "Reservar",
    },
    portfolio: {
      title: "Nuestro",
      titleAccent: "Trabajo",
      subtitle: "Una muestra de nuestros mejores cortes y estilos",
      kicker: "NUESTRO TRABAJO",
    },
    team: {
      title: "Conoce al",
      titleAccent: "Equipo",
      subtitle: "Profesionales dedicados a tu mejor estilo",
      members: [
        {
          name: "Marriel",
          role: "Barbero Maestro / Fundador",
          description:
            "Especialista en cortes modernos con acabado preciso y atención al detalle.",
          imageKey: "marriel",
        },
      ],
    },
    visit: {
      title: "Visítanos",
      titleAccent: "",
      subtitle: "Esperamos darte la bienvenida",
      ourLocation: "Ubicación",
      openingHours: "Horario",
      getDirections: "Cómo llegar",
      closed: "Cerrado",
      kicker: "VISÍTANOS",
      days: {
        monday: "Lun",
        tuesday: "Mar",
        wednesday: "Mié",
        thursday: "Jue",
        friday: "Vie",
        saturday: "Sáb",
        sunday: "Dom",
      },
    },
    footer: {
      rights: "© Marriel Barber Shop. Todos los derechos reservados.",
    },
    notFound: {
      title: "404",
      message: "Ups! Página no encontrada",
      backHome: "Volver al inicio",
    },
  },
} as const satisfies Record<Language, Translations>;
